/**
 * Sistema de Métricas e Observabilidade - ETAPA 2
 * 
 * Rastreia uso de backend vs fallback, tempos de resposta e erros
 */

import { FEATURE_FLAGS } from '../config/features';

/**
 * Métricas globais (expostas em window.BACKEND_METRICS)
 */
class MetricsCollector {
  constructor() {
    this.metrics = {
      requests: {
        total: 0,
        backend: 0,
        fallback: 0,
        failed: 0
      },
      responseTime: {
        backend: [],
        fallback: []
      },
      errors: {
        timeout: 0,
        network: 0,
        server: 0,
        auth: 0,
        unknown: 0
      },
      endpoints: {}
    };

    if (FEATURE_FLAGS.enableMetrics) {
      window.BACKEND_METRICS = this.getMetrics.bind(this);
    }
  }

  /**
   * Registra requisição ao backend
   */
  recordBackendRequest(endpoint, duration, success) {
    if (!FEATURE_FLAGS.enableMetrics) return;

    this.metrics.requests.total++;
    this.metrics.requests.backend++;
    
    if (success) {
      this.metrics.responseTime.backend.push(duration);
    } else {
      this.metrics.requests.failed++;
    }

    if (!this.metrics.endpoints[endpoint]) {
      this.metrics.endpoints[endpoint] = { count: 0, avgTime: 0, errors: 0 };
    }
    this.metrics.endpoints[endpoint].count++;
    
    if (!success) {
      this.metrics.endpoints[endpoint].errors++;
    }
  }

  /**
   * Registra uso de fallback
   */
  recordFallback(endpoint, reason, duration) {
    if (!FEATURE_FLAGS.enableMetrics) return;

    this.metrics.requests.fallback++;
    this.metrics.responseTime.fallback.push(duration);

    if (!this.metrics.endpoints[endpoint]) {
      this.metrics.endpoints[endpoint] = { count: 0, avgTime: 0, errors: 0, fallbacks: 0 };
    }
    this.metrics.endpoints[endpoint].fallbacks = (this.metrics.endpoints[endpoint].fallbacks || 0) + 1;
  }

  /**
   * Registra erro por tipo
   */
  recordError(errorType) {
    if (!FEATURE_FLAGS.enableMetrics) return;

    const type = errorType.toLowerCase();
    if (type.includes('timeout')) {
      this.metrics.errors.timeout++;
    } else if (type.includes('network') || type.includes('fetch')) {
      this.metrics.errors.network++;
    } else if (type.includes('401') || type.includes('autenticação')) {
      this.metrics.errors.auth++;
    } else if (type.includes('500') || type.includes('502') || type.includes('503')) {
      this.metrics.errors.server++;
    } else {
      this.metrics.errors.unknown++;
    }
  }

  /**
   * Retorna estatísticas calculadas
   */
  getMetrics() {
    const backendP95 = this.calculatePercentile(this.metrics.responseTime.backend, 95);
    const fallbackP95 = this.calculatePercentile(this.metrics.responseTime.fallback, 95);

    return {
      summary: {
        totalRequests: this.metrics.requests.total,
        backendRequests: this.metrics.requests.backend,
        fallbackRequests: this.metrics.requests.fallback,
        failedRequests: this.metrics.requests.failed,
        backendUsageRate: this.metrics.requests.total > 0 
          ? ((this.metrics.requests.backend / this.metrics.requests.total) * 100).toFixed(2) + '%'
          : '0%',
        fallbackRate: this.metrics.requests.total > 0
          ? ((this.metrics.requests.fallback / this.metrics.requests.total) * 100).toFixed(2) + '%'
          : '0%'
      },
      performance: {
        backendP95: backendP95 ? backendP95.toFixed(0) + 'ms' : 'N/A',
        fallbackP95: fallbackP95 ? fallbackP95.toFixed(0) + 'ms' : 'N/A',
        backendAvg: this.calculateAverage(this.metrics.responseTime.backend),
        fallbackAvg: this.calculateAverage(this.metrics.responseTime.fallback)
      },
      errors: this.metrics.errors,
      endpoints: this.metrics.endpoints
    };
  }

  /**
   * Calcula percentil (ex: P95)
   */
  calculatePercentile(arr, percentile) {
    if (!arr || arr.length === 0) return null;
    const sorted = [...arr].sort((a, b) => a - b);
    const index = Math.ceil((percentile / 100) * sorted.length) - 1;
    return sorted[index];
  }

  /**
   * Calcula média
   */
  calculateAverage(arr) {
    if (!arr || arr.length === 0) return 'N/A';
    const sum = arr.reduce((acc, val) => acc + val, 0);
    return (sum / arr.length).toFixed(0) + 'ms';
  }

  /**
   * Reseta métricas
   */
  reset() {
    this.metrics = {
      requests: { total: 0, backend: 0, fallback: 0, failed: 0 },
      responseTime: { backend: [], fallback: [] },
      errors: { timeout: 0, network: 0, server: 0, auth: 0, unknown: 0 },
      endpoints: {}
    };
  }
}

/**
 * Instância global do coletor de métricas
 */
export const metrics = new MetricsCollector();

/**
 * Sistema de Logs Estruturados
 */
export class Logger {
  /**
   * Log de requisição ao backend (sucesso)
   */
  static logBackendRequest(method, endpoint, duration) {
    if (FEATURE_FLAGS.logLevel === 'none') return;
    if (FEATURE_FLAGS.logLevel === 'all') {
      console.log(
        `%c[Backend] %c${method} ${endpoint} %c✓ ${duration}ms`,
        'color: #10b981; font-weight: bold',
        'color: #6b7280',
        'color: #10b981'
      );
    }
  }

  /**
   * Log de erro no backend
   */
  static logBackendError(method, endpoint, error, duration) {
    if (FEATURE_FLAGS.logLevel === 'none') return;
    
    console.error(
      `%c[Backend Error] %c${method} ${endpoint} %c✗ ${duration}ms`,
      'color: #ef4444; font-weight: bold',
      'color: #6b7280',
      'color: #ef4444'
    );
    console.error('  └─', error.message || error);
  }

  /**
   * Log de fallback para Supabase
   * 
   * CONSOLIDAÇÃO ETAPA 3: Elevado para WARNING crítico
   */
  static logFallback(endpoint, reason, duration) {
    if (FEATURE_FLAGS.logLevel === 'none') return;

    // SEMPRE loggar fallback (mesmo em modo 'errors')
    // Fallback é evento crítico que deve ser visível
    console.warn(
      `%c⚠️ [FALLBACK CRÍTICO] %c${endpoint} %c→ Supabase direto (${duration}ms)`,
      'color: #ff6b00; font-weight: bold; background: #fff3cd; padding: 2px 4px;',
      'color: #6b7280',
      'color: #ff6b00; font-weight: bold'
    );
    console.warn('  └─ Motivo:', reason);
    console.warn('  └─ ⚠️ ATENÇÃO: Backend indisponível. Sistema usando failsafe.');
  }

  /**
   * Log de retry
   */
  static logRetry(attempt, maxRetries, endpoint) {
    if (FEATURE_FLAGS.logLevel === 'none') return;
    if (FEATURE_FLAGS.logLevel === 'all') {
      console.log(
        `%c[Retry] %cTentativa ${attempt}/${maxRetries} - ${endpoint}`,
        'color: #3b82f6; font-weight: bold',
        'color: #6b7280'
      );
    }
  }

  /**
   * Log de timeout
   */
  static logTimeout(endpoint, timeout) {
    if (FEATURE_FLAGS.logLevel === 'none') return;

    console.warn(
      `%c[Timeout] %c${endpoint} %cexcedeu ${timeout}ms`,
      'color: #f59e0b; font-weight: bold',
      'color: #6b7280',
      'color: #f59e0b'
    );
  }

  /**
   * Log de métricas (resumo)
   */
  static logMetricsSummary() {
    if (!FEATURE_FLAGS.enableMetrics) return;
    if (FEATURE_FLAGS.logLevel === 'none') return;

    const summary = metrics.getMetrics();
    console.group('%c📊 Métricas Backend', 'color: #8b5cf6; font-weight: bold');
    console.table(summary.summary);
    console.table(summary.performance);
    console.table(summary.errors);
    console.groupEnd();
  }
}

/**
 * Expor comando para ver métricas no console
 */
if (FEATURE_FLAGS.enableMetrics) {
  window.showBackendMetrics = () => Logger.logMetricsSummary();
}
